import { Box, ButtonBase, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import Typography from '@mui/material/Typography/Typography';
import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import TooltipHardened from '../TooltipHardened';
import useNavigation from '../../Providers/NavigationProvider';
import {
  NAV_BG_ACTIVE,
  NAV_BG_HOVER,
  NAV_COLOR_ACTIVE,
  NAV_COLOR_INACTIVE,
  NAV_ICON_MIN_WIDTH,
  NAV_LABEL_FONT_SIZE,
  NAV_LABEL_FONT_WEIGHT,
  NAV_STRIPE_COLOR,
  NAV_STRIPE_WIDTH,
  navPillSx,
} from './navItemStyle';

/**
 * Renders the underlying clickable element as either a react-router
 * `<Link>` (internal `to`) or a plain `<a target="_blank">` (external
 * `href`). Both produce real anchor tags, so the browser handles
 * Cmd-click / Ctrl-click / middle-click / "Open in new tab" natively
 * across every OS and browser — we don't write any modifier-key code.
 *
 * Exactly one of `to` or `href` must be passed.
 */
type NavItemProps = {
  icon: ReactNode;
  label: string;
  /** Render the item as the currently-active route. */
  isActive?: boolean;
} & ({ to: string; href?: never } | { to?: never; href: string });

export const NavItem = ({ icon, label, isActive = false, to, href }: NavItemProps): React.JSX.Element => {
  const { isOpen } = useNavigation();

  // External links open in a new tab; internal links use client-side
  // routing via react-router's Link (which still renders an <a href>,
  // so modifier-clicks pass through to the browser).
  const linkProps =
    to !== undefined
      ? ({ component: Link, to } as const)
      : ({ component: 'a' as const, href, target: '_blank', rel: 'noopener noreferrer' } as const);

  if (!isOpen) {
    return (
      <TooltipHardened
        tooltip={
          <Typography fontSize={16} fontWeight={700}>
            {label}
          </Typography>
        }
        enterDelay={150}
        leaveDelay={100}
        placement={'right'}
      >
        <ButtonBase
          {...linkProps}
          aria-current={isActive ? 'page' : undefined}
          sx={{
            width: '100%',
            py: 1,
            display: 'flex',
            justifyContent: 'center',
            // Active state: tinted background marks "where you are" in
            // icon-only mode; the icon stays neutral (not primary). Inactive
            // icons are muted so the active one still stands out.
            color: isActive ? NAV_COLOR_ACTIVE : NAV_COLOR_INACTIVE,
            backgroundColor: isActive ? NAV_BG_ACTIVE : 'transparent',
            '&:hover': { backgroundColor: isActive ? NAV_BG_ACTIVE : NAV_BG_HOVER },
          }}
        >
          {icon}
        </ButtonBase>
      </TooltipHardened>
    );
  }

  return (
    <ListItem disableGutters disablePadding>
      <ListItemButton
        {...linkProps}
        selected={isActive}
        aria-current={isActive ? 'page' : undefined}
        sx={{
          // Shared pill shape (flush + squared against the left border, inset +
          // rounded on the right) + the shared transition. Living in the base
          // means hover and selection render the same shape.
          ...navPillSx('left'),
          // Underline-on-hover is the browser default for anchors; we
          // explicitly clear it so the nav doesn't pick up a stray
          // underline now that the item renders as <a>.
          textDecoration: 'none',
          color: 'inherit',
          // Inactive icon + label are muted to match the Settings tabs; the
          // active item flips both to the active color below.
          '& .MuiListItemIcon-root': { color: NAV_COLOR_INACTIVE },
          // Same label typography as the Settings tabs (size + weight + color).
          '& .MuiListItemText-primary': {
            fontSize: NAV_LABEL_FONT_SIZE,
            fontWeight: NAV_LABEL_FONT_WEIGHT,
            color: NAV_COLOR_INACTIVE,
          },
          '&:hover': { textDecoration: 'none', backgroundColor: NAV_BG_HOVER },
          // Active: filled pill with neutral icon + text (the accent is the
          // stripe). Same shape as hover, just the active background + colors.
          '&.Mui-selected': {
            backgroundColor: NAV_BG_ACTIVE,
            '&:hover': { backgroundColor: NAV_BG_ACTIVE },
            '& .MuiListItemIcon-root': { color: NAV_COLOR_ACTIVE },
            '& .MuiListItemText-primary': { color: NAV_COLOR_ACTIVE },
          },
        }}
      >
        {/* Full-height accent stripe at the drawer's left border (the active
            pill is flush there), flush against the pill's squared left edge. */}
        {isActive && (
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: NAV_STRIPE_WIDTH,
              backgroundColor: NAV_STRIPE_COLOR,
            }}
          />
        )}
        <ListItemIcon sx={{ minWidth: NAV_ICON_MIN_WIDTH, py: 1 }}>{icon}</ListItemIcon>
        <ListItemText sx={{ textDecoration: 'none' }}>{label}</ListItemText>
      </ListItemButton>
    </ListItem>
  );
};
